import React from "react";
import {
  createStyles,
  UnstyledButton,
  Group,
  Text,
  Center,
  Flex,
  ThemeIcon,
  ActionIcon,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconArrowsSort,
  IconArrowUp,
  IconArrowDown,
} from "@tabler/icons";
import {
  ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header,
  Table,
  useReactTable,
} from "@tanstack/react-table";

export interface IThProps {
  table: Table<unknown>;
  header: Header<unknown, unknown>;
  sortable?: boolean;
  resizable?: boolean;
  columnResizeMode?: ColumnResizeMode;
}

const useStyles = createStyles((theme, _params: any, getRef) => ({
  th: {
    padding: "0 !important",
    width: _params.header.getSize(),
    position: "relative",

    "&:hover": {
      [`& .${getRef("resizer")}`]: {
        backgroundColor: theme.fn.rgba(theme.colors.blue[5], 0.5),
      },
    },
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.md}px ${theme.spacing.md}px`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },

  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  resizer: {
    ref: getRef("resizer"),
    top: 0,
    right: 0,
    bottom: 0,
    width: 3,
    position: "absolute",
    cursor: "col-resize",
  },
}));

export const Th = ({
  table,
  header,
  sortable,
  columnResizeMode,
  resizable,
}: IThProps) => {
  const { classes, cx } = useStyles({ header: header });

  const Icon = () => {
    const sortOrder = header.column.getIsSorted();

    if (sortable) {
      switch (sortOrder) {
        case "asc":
          return (
            <ActionIcon m={0}>
              <IconArrowUp size={16} stroke={2} />
            </ActionIcon>
          );
        case "desc":
          return (
            <ActionIcon m={0}>
              <IconArrowDown size={16} stroke={2} />
            </ActionIcon>
          );
        default:
          return (
            <ActionIcon m={0} color="gray.4">
              <IconArrowsSort size={16} stroke={2} />
            </ActionIcon>
          );
      }
    }
    return null;
  };

  return (
    <th
      key={header.id}
      className={classes.th}
      // {...(sortable && {
      //   className: header.column.getCanSort() ? "column-header-sortable" : "",
      //   //onClick: header.column.getToggleSortingHandler(),
      // })}
      style={{
        width: header.getSize(),
      }}
    >
      <Flex>
        {header.column.columnDef.header &&
          (sortable ? (
            <UnstyledButton
              onClick={header.column.getToggleSortingHandler()}
              className={classes.control}
              sx={(theme) => ({
                padding: theme.spacing.xs,
              })}
            >
              <Group sx={{ flexWrap: "nowrap" }} spacing={0}>
                <Text weight={500} size="xs" lineClamp={1}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Text>
                <Icon />
              </Group>
            </UnstyledButton>
          ) : (
            <UnstyledButton
              className={classes.control}
              sx={(theme) => ({
                padding: theme.spacing.xs,
              })}
            >
              <Group sx={{ flexWrap: "nowrap" }} spacing={0}>
                <Text weight={500} size="xs" lineClamp={1}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Text>
              </Group>
            </UnstyledButton>
          ))}
        {resizable && (
          <div
            {...{
              onMouseDown: header.getResizeHandler(),
              onTouchStart: header.getResizeHandler(),
              className: cx(classes.resizer, {
                isResizing: header.column.getIsResizing(),
              }),
              style: {
                transform:
                  columnResizeMode === "onEnd" && header.column.getIsResizing()
                    ? `translateX(${
                        table.getState().columnSizingInfo.deltaOffset
                      }px)`
                    : "",
              },
            }}
          />
        )}
      </Flex>
    </th>
  );
};
